import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Eye, EyeOff, Edit, Trash2, Key, Copy, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Credentials = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [formData, setFormData] = useState({
    system_name: '',
    username: '',
    password: '',
    notes: '',
    url: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      fetchCredentials();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/credentials`, {
        withCredentials: true
      });
      setCredentials(response.data);
    } catch (error) {
      toast.error('Failed to fetch credentials');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      system_name: '',
      username: '',
      password: '',
      notes: '',
      url: ''
    });
    setEditingCredential(null);
  };

  const handleEdit = (credential) => {
    setEditingCredential(credential);
    setFormData({
      system_name: credential.system_name,
      username: credential.username,
      password: '', // Don't pre-fill password for security
      notes: credential.notes || '',
      url: credential.url || ''
    });
    setShowCreateDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCredential) {
        // Only send fields that were changed
        const updateData = {};
        if (formData.system_name !== editingCredential.system_name) updateData.system_name = formData.system_name;
        if (formData.username !== editingCredential.username) updateData.username = formData.username;
        if (formData.password) updateData.password = formData.password; // Only if password was entered
        if (formData.notes !== (editingCredential.notes || '')) updateData.notes = formData.notes;
        if (formData.url !== (editingCredential.url || '')) updateData.url = formData.url;

        await axios.put(
          `${BACKEND_URL}/api/credentials/${editingCredential.credential_id}`,
          updateData,
          { withCredentials: true }
        );
        toast.success('Credential updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/credentials`,
          formData,
          { withCredentials: true }
        );
        toast.success('Credential created successfully');
      }

      setShowCreateDialog(false);
      resetForm();
      fetchCredentials();
    } catch (error) {
      console.error('Error saving credential:', error);
      toast.error(error.response?.data?.detail || 'Failed to save credential');
    }
  };

  const handleDelete = async (credentialId) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/credentials/${credentialId}`,
        { withCredentials: true }
      );
      toast.success('Credential deleted successfully');
      fetchCredentials();
    } catch (error) {
      toast.error('Failed to delete credential');
    }
  };

  const handleRevealPassword = async (credentialId) => {
    if (revealedPasswords[credentialId]) {
      // Hide password
      setRevealedPasswords(prev => ({ ...prev, [credentialId]: null }));
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/credentials/${credentialId}/reveal`,
        { withCredentials: true }
      );
      setRevealedPasswords(prev => ({ ...prev, [credentialId]: response.data.password }));
    } catch (error) {
      toast.error('Failed to reveal password');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex h-16 items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="font-black text-xl uppercase tracking-tight">
              NEXT<span className="text-primary">/</span>GEN
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 md:px-12 py-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 sm:mb-12">
            <div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-black uppercase mb-2">
                SYSTEM CREDENTIALS
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Securely store system passwords ({credentials.length}/20)
              </p>
            </div>

            <Dialog open={showCreateDialog} onOpenChange={(open) => {
              setShowCreateDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button 
                  className="font-bold uppercase w-full sm:w-auto" 
                  disabled={credentials.length >= 20}
                  data-testid="add-credential-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Credential
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border mx-4 sm:mx-0 max-w-lg sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold uppercase">
                    {editingCredential ? 'Edit Credential' : 'Add Credential'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>System Name *</Label>
                    <Input
                      value={formData.system_name}
                      onChange={(e) => setFormData({ ...formData, system_name: e.target.value })}
                      required
                      placeholder="e.g., AWS Console, Office 365, Router Admin"
                      className="bg-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Username *</Label>
                      <Input
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required
                        placeholder="admin@example.com"
                        className="bg-input"
                      />
                    </div>
                    <div>
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!editingCredential}
                        placeholder={editingCredential ? "Leave blank to keep" : "••••••••"}
                        className="bg-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>URL (Optional)</Label>
                    <Input
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://console.aws.amazon.com"
                      className="bg-input"
                    />
                  </div>

                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      placeholder="Additional information, access instructions, etc."
                      className="bg-input"
                    />
                  </div>

                  <Button type="submit" className="w-full font-bold uppercase">
                    {editingCredential ? 'Update Credential' : 'Save Credential'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {credentials.length === 0 ? (
            <Card className="p-12 text-center border-border">
              <Key className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">No credentials stored yet</p>
              <p className="text-sm text-muted-foreground">
                Add your system passwords and credentials to access them securely from anywhere
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {credentials.map((credential, index) => (
                <motion.div
                  key={credential.credential_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                >
                  <Card className="p-4 sm:p-6 border-border hover:border-primary/50 transition-colors">
                    <div className="flex items-start justify-between mb-3 sm:mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold mb-1 truncate">{credential.system_name}</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground font-mono truncate">{credential.username}</p>
                      </div>
                      <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(credential)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(credential.credential_id)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted/30 px-2 sm:px-3 py-2 rounded-sm font-mono text-xs sm:text-sm truncate">
                          {revealedPasswords[credential.credential_id] || '••••••••••••'}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevealPassword(credential.credential_id)}
                          className="h-8 w-8 p-0 flex-shrink-0"
                        >
                          {revealedPasswords[credential.credential_id] ? (
                            <EyeOff className="w-3 h-3 sm:w-4 sm:h-4" />
                          ) : (
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                        </Button>
                        {revealedPasswords[credential.credential_id] && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(revealedPasswords[credential.credential_id], 'Password')}
                            className="h-8 w-8 p-0 flex-shrink-0"
                          >
                            <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        )}
                      </div>

                      {credential.url && (
                        <div className="flex items-center gap-2">
                          <a
                            href={credential.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            {credential.url}
                          </a>
                        </div>
                      )}

                      {credential.notes && (
                        <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-sm">
                          {credential.notes}
                        </div>
                      )}

                      <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                        Added: {new Date(credential.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {credentials.length >= 20 && (
            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/50 rounded-sm">
              <p className="text-sm text-yellow-500">
                You've reached the maximum of 20 stored credentials. Delete unused credentials to add new ones.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Credentials;
